import { JourneySummary, journeySummaryFromTrip } from "./JourneySummary";
import { DeviceDataPanel } from "./DeviceDataPanel";
import { AppIssueForm } from "./AppIssueForm";
import { SavedWalkSuggestions, TravelCollections } from "./TravelCollections";
import { SummaryCard } from "./SummaryCard";
import {
	applyPastedTrip,
	parsePastedTrip,
	type PastedStop,
	isTripPlanItem,
} from "@japangolmok/core";
import { TodayTools, DayMap } from "./TodayTools";
import { PreparationTools, StayEditor, ReturnTime } from "./PreparationTools";
import { InstallPrompt } from "../ui/InstallPrompt";
import { MoneyPanel } from "./MoneyPanel";
import { ShareSheet, PartyEditor, PasteShare } from "./ShareSheet";
import { TripEditor, DaySwap } from "./TripEditor";
import { trashTrip } from "../storage/trash";
import { TrashPanel } from "./TrashPanel";
import { useEffect, useMemo, useRef, useState } from "react";
import {
	cityGuides,
	packingSuggestions,
	editions,
	essentialPhrases,
	googleMapsSearchUrl,
	officialTravelTools,
	placeCandidates,
	recommendTripDays,
	origins,
	routeSnapshotMeta,
	tripCompanions,
	type AirportCode,
	type CompanionAirportCode,
	type OriginCode,
} from "@japangolmok/content";
import {
	createTripWorkspace,
	jpyToKrw,
	personalFareSummary,
	settleExpenses,
	tripParty,
	tripProgress,
	tripStage,
	type TripPlanItem,
	type TripStage,
	type TripStyle,
	type TripWorkspace,
} from "@japangolmok/core";
import { track } from "../analytics/events";
import {
	disabledPublisherAdAdapter,
	StandardPublisherAdSlot,
	type PublisherAdAdapter,
} from "../ads/publisher-ad";
import {
	customerSectionLabels,
	tripDayLabel,
} from "../content/customer-language";
import { listTrips, removeTrip, saveTrip } from "../storage/trips";
import { BrandMark } from "../ui/BrandMark";
import { ExternalLink, Layout, SourceStamp, usePageMeta } from "../ui/Shell";
import {
	TripImportPanel,
	TripExportPanel,
	TripClonePanel,
} from "./TripTransfer";
import {
	DayPlan,
	LocalSaveStatus,
	TripCandidates,
	TripRecommendations,
	TripContextEditor,
	candidateToStop,
} from "./TripExperience";
import { getWalk } from "@japangolmok/content";
import { appendTripStops } from "@japangolmok/core";
import {
	LoadingTrip,
	MissingTrip,
	TripStorageError,
	useTripWorkspace,
} from "./useTripWorkspace";

const destinationCodes = Object.keys(editions) as AirportCode[];
const styleOptions: { value: TripStyle; label: string }[] = [
	{ value: "food", label: "먹거리" },
	{ value: "culture", label: "문화·역사" },
	{ value: "nature", label: "자연" },
	{ value: "shopping", label: "쇼핑" },
	{ value: "family", label: "가족" },
	{ value: "slow", label: "느린 산책" },
];
const stageCopy: Record<TripStage, string> = {
	planning: "천천히 계획 중",
	upcoming: "출발이 가까워요",
	traveling: "여행 중",
	completed: "다녀온 여행",
	archived: "보관한 여행",
};
const itemTypeCopy = {
	spot: "가볼 곳",
	food: "먹을 것",
	transport: "이동",
	memo: "메모",
} as const;
const categoryCopy = {
	transport: "교통",
	food: "식사",
	stay: "숙박",
	shopping: "쇼핑",
	experience: "경험",
	other: "기타",
} as const;
const tripPanels = [
	{ id: "overview", label: "여행 한눈에" },
	{ id: "itinerary", label: "일정" },
	{ id: "prepare", label: "출발 준비" },
	{ id: "budget", label: "돈" },
	{ id: "keep", label: "보관과 다시 쓰기" },
] as const;
type TripPanel = (typeof tripPanels)[number]["id"];
const panelFromHash = (hash: string): TripPanel => {
	const candidate = hash === "#fare-notes" ? "budget" : hash === "#calendar" ? "prepare" : hash.replace(/^#/, "");
	return tripPanels.some((panel) => panel.id === candidate)
		? (candidate as TripPanel)
		: "overview";
};

const todayInSeoul = () => {
	const parts = new Intl.DateTimeFormat("en", {
		timeZone: "Asia/Seoul",
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).formatToParts(new Date());
	const value = Object.fromEntries(
		parts.map((part) => [part.type, part.value]),
	);
	return `${value.year}-${value.month}-${value.day}`;
};
const dateLabel = (value: string) =>
	new Intl.DateTimeFormat("ko-KR", {
		month: "short",
		day: "numeric",
		weekday: "short",
		timeZone: "UTC",
	}).format(new Date(`${value}T00:00:00Z`));
const money = (value: number, currency: "JPY" | "KRW") =>
	new Intl.NumberFormat("ko-KR", {
		style: "currency",
		currency,
		maximumFractionDigits: 0,
	}).format(value);
const addCalendarDays = (value: string, offset: number) => {
	const date = new Date(`${value}T00:00:00Z`);
	if (Number.isNaN(date.getTime())) return "";
	date.setUTCDate(date.getUTCDate() + offset);
	return date.toISOString().slice(0, 10);
};

export function TripListPage() {
	const [trips, setTrips] = useState<TripWorkspace[] | null>(null);
	const [error, setError] = useState("");
	const [readAttempt, setReadAttempt] = useState(0);
	usePageMeta(
		"내 여행 — 일본골목",
		"준비 중인 일본 여행과 다녀온 기록을 이 기기에서 이어 가요.",
	);
	useEffect(() => {
		setError("");
		listTrips()
			.then(setTrips)
			.catch(() => setError("이 기기의 여행 저장소를 읽지 못했어요. 사이트 데이터 허용과 저장 공간을 확인하고 다른 탭을 닫은 뒤 다시 시도해 주세요. 기록은 지우지 않아요."));
	}, [readAttempt]);
	return (
		<Layout>
			<section className="route-head compact paper-pattern">
				<span className="eyebrow">내 여행 · 이 기기에 보관</span>
				<h1>
					여행이 시작된 뒤에도
					<br />
					여기서 계속해요.
				</h1>
				<p>일정·준비·예산·도착 첫 길을 계정 없이 이 기기에 보관해요.</p>
				<div className="button-row">
					{error ? <button className="button primary" type="button" onClick={() => setReadAttempt((n) => n + 1)}>여행 다시 읽기</button> : <a className="button primary" href="/trips/new">새 여행 만들기</a>}
				</div>
				{error && <p className="inline-error" role="alert">{error}</p>}
			</section>
			<section className="section trip-list-section">
				<div className="home-tools">
					<a href="/today">오늘 필요한 여행 바로 열기 →</a>
					<a href="/cards">저장한 공항 첫 길 →</a>
					<a href="/companions">한 코스 동행 →</a>
				</div>
				<details className="workspace-disclosure">
					<summary>다른 기기의 여행 백업 파일 복원하기</summary>
					<TripImportPanel
						onImported={async () => {
							setTrips(await listTrips());
						}}
					/>
				</details>
				{trips === null && !error && (
					<div className="skeleton-list" aria-label="여행 목록을 불러오는 중" />
				)}
				{trips?.length === 0 && (
					<div className="empty-state">
						<BrandMark size={72} />
						<h2>첫 여행을 만들어 볼까요?</h2>
						<p>
							출발공항과 일본 공항, 날짜만 정하면 지역별 추천과 준비 체크가
							생겨요.
						</p>
						<a className="button primary" href="/trips/new">
							3분 안에 여행 만들기
						</a>
					</div>
				)}
				<DeviceDataPanel onChanged={async () => setTrips(await listTrips())} />
				<PasteShare />
				<TrashPanel onRestored={async () => setTrips(await listTrips())} />
				{trips && (
					<TravelCollections
						trips={trips}
						onChanged={async () => setTrips(await listTrips())}
					/>
				)}
				<div className="trip-card-grid">
					{trips?.map((trip) => {
						const stage = tripStage(trip, todayInSeoul());
						const progress = tripProgress(trip);
						return (
							<a
								className="trip-card"
								href={`/trips/${encodeURIComponent(trip.id)}`}
								key={trip.id}
							>
								<span className={`status-badge stage-${stage}`}>
									{stageCopy[stage]}
								</span>
								<div className="trip-route">
									<span>{trip.origin}</span>
									<i />
									<span>{trip.destination}</span>
								</div>
								<h2>{trip.title}</h2>
								<p>
									{dateLabel(trip.startDate)}–{dateLabel(trip.endDate)} ·{" "}
									{trip.partySize}명
								</p>
								<div className="progress-line">
									<span
										style={{
											width: `${progress.checklistTotal ? (progress.checklistDone / progress.checklistTotal) * 100 : 0}%`,
										}}
									/>
								</div>
								<small>
									준비 {progress.checklistDone}/{progress.checklistTotal} · 일정{" "}
									{progress.plannedDays}/{progress.totalDays}일
								</small>
							</a>
						);
					})}
				</div>
				<aside className="privacy-note">
					<strong>왜 로그인이 없나요?</strong>
					<p>
						여행 제목·날짜·지출·메모는 민감할 수 있어 기본값으로 서버에 보내지
						않아요. 기기를 바꾸기 전 여행 백업 파일을 받아 두세요.
					</p>
				</aside>
			</section>
		</Layout>
	);
}

const CREATE_DRAFT_KEY = "jgm-create-draft";
function readCreateDraft(seed: string): Record<string, string[]> {
	try {
		const value = JSON.parse(sessionStorage.getItem(CREATE_DRAFT_KEY) ?? "{}");
		if (!value || typeof value !== "object" || Array.isArray(value)) return {};
		if (typeof value.seed === "string") return value.seed === seed && value.entries && typeof value.entries === "object" ? value.entries : {};
		return seed ? {} : value;
	} catch { return {}; }
}

export function TripCreatePage({ embedded = false }: { embedded?: boolean } = {}) {
	const params = new URLSearchParams(window.location.search);
	params.sort();
	const seed = params.toString();
	const [draft] = useState(() => readCreateDraft(seed));
	const formRef = useRef<HTMLFormElement>(null);
	const initialOrigin = origins.some(
		(item) => item.iata === params.get("origin"),
	)
		? (params.get("origin") as OriginCode)
		: "PUS";
	const initialDestination = destinationCodes.includes(
		(params.get("destination") ?? "") as AirportCode,
	)
		? (params.get("destination") as AirportCode)
		: "FUK";
	const requestedDays = Math.min(
		5,
		Math.max(1, Math.trunc(Number(params.get("days")) || 3)),
	);
	const [pasteText, setPasteText] = useState(() => { try { const text = sessionStorage.getItem("jgm-paste-text") ?? ""; sessionStorage.removeItem("jgm-paste-text"); return text; } catch { return ""; } });
	const [pasteRows, setPasteRows] = useState<PastedStop[] | null>(() => {
		try {
			if (!params.has("pasteDraft")) return null;
			const rows = JSON.parse(
				sessionStorage.getItem("jgm-paste-draft") ?? "null",
			);
			sessionStorage.removeItem("jgm-paste-draft");
			return Array.isArray(rows)
				? rows.map((row) => ({ ...row, localKey: crypto.randomUUID() }))
				: null;
		} catch {
			return null;
		}
	});
	const [destination, setDestination] =
		useState<AirportCode>(destinationCodes.includes(draft.destination?.[0] as AirportCode) ? draft.destination![0] as AirportCode : initialDestination);
	const [initialFeaturedDestination] = useState(destination);
	const submitting = useRef(false);
	const [saving, setSaving] = useState(false);
	const initialStartDate = /^\d{4}-\d{2}-\d{2}$/.test(params.get("startDate") ?? "") ? params.get("startDate")! : "";
	const [startDate, setStartDate] = useState(draft.startDate?.[0] ?? initialStartDate);
	const [endDate, setEndDate] = useState(draft.endDate?.[0] ?? (initialStartDate ? addCalendarDays(initialStartDate, requestedDays - 1) : ""));
	const [error, setError] = useState("");
	usePageMeta(
		"새 여행 만들기 — 일본골목",
		"출발공항, 일본 공항, 날짜와 취향으로 내 여행을 만들어요.",
		!embedded,
	);
	useEffect(() => track("trip_create_start", { surface: "trip_create" }), []);
	useEffect(() => {
		const form = formRef.current;
		if (!form) return;
		for (const element of Array.from(form.elements)) {
			if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement) || !element.name || !Array.isArray(draft[element.name])) continue;
			if (element instanceof HTMLInputElement && ["checkbox", "radio"].includes(element.type)) element.checked = draft[element.name]!.includes(element.value);
			else if (!["destination", "startDate", "endDate"].includes(element.name)) element.value = draft[element.name]![0] ?? "";
		}
	}, [draft]);
	const saveDraft = (form: HTMLFormElement, target: EventTarget) => {
		try {
			const entries: Record<string, string[]> = {};
			for (const [name, value] of new FormData(form)) if (typeof value === "string") (entries[name] ??= []).push(value);
			if (target instanceof HTMLInputElement && target.name === "startDate") entries.endDate = [target.value ? addCalendarDays(target.value, requestedDays - 1) : ""];
			sessionStorage.setItem(CREATE_DRAFT_KEY, JSON.stringify({ seed, entries }));
		} catch { /* 입력은 현재 화면에 유지 */ }
	};
	const submit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (submitting.current) return;
		submitting.current = true;
		setSaving(true);
		setError("");
		const data = new FormData(event.currentTarget);
		try {
			const partyNames = String(data.get("partyNames") ?? "").split(",").map(name => name.trim()).filter(Boolean);
            if (partyNames.length > 12 || partyNames.some(name => name.length > 30)) { setError("같이 가는 사람은 12명까지, 이름은 30자 이내로 적어 주세요."); return; }
			const trip = createTripWorkspace(
				{
					title: String(data.get("title") ?? ""),
					origin: String(data.get("origin") ?? ""),
					destination,
					destinationCity: editions[destination].city,
					startDate: String(data.get("startDate") ?? ""),
					endDate: String(data.get("endDate") ?? ""),
					partySize: Math.max(Number(data.get("partySize")), partyNames.length),
					styles: data.getAll("styles") as TripStyle[],
					dailyBudgetJpy: Number(data.get("dailyBudgetJpy")),
					krwPer100Jpy: Number(data.get("krwPer100Jpy")) || null,
				},
				new Date().toISOString(),
			);
            if (partyNames.length) trip.party = Array.from({length: trip.partySize}, (_, i) => ({id: i === 0 ? "me" : `party-${i}`, nickname: partyNames[i] || (i === 0 ? "나" : `일행${i}`)}));
			const walk = getWalk(params.get("walk") ?? "");
			if (walk && !walk.airportCodes.includes(destination)) {
				setError(
					"고른 코스와 도착공항이 달라요. 코스에 연결된 공항을 고르거나, 반나절 탐색에서 다른 코스를 선택해 주세요.",
				);
				return;
			}
			if (walk) {
				const selected = (params.get("places") ?? "").split(",");
				const places = [
					...walk.stops,
					...walk.mealOptions,
					...walk.restOptions,
				].filter((p) => selected.includes(p.placeId));
				Object.assign(
					trip,
					appendTripStops(
						trip,
						trip.startDate,
						places.map((p) => candidateToStop(p, walk.id)),
					),
				);
			}
			if (!walk && params.has("places")) {
				const requested = [
					...new Set((params.get("places") ?? "").split(",").filter(Boolean)),
				];
				const available = placeCandidates(destination);
				const places = available.filter((place) =>
					requested.includes(place.placeId),
				);
				if (!requested.length || places.length !== requested.length) {
					setError(
						"고른 장소를 찾지 못했어요. 동네 탐색에서 다시 골라 주세요.",
					);
					return;
				}
				Object.assign(
					trip,
					appendTripStops(
						trip,
						trip.startDate,
						places.map((place) => candidateToStop(place)),
					),
				);
			} else if (!walk) {
				const recommendations = recommendTripDays(
					destination,
					trip.styles,
					trip.days.length,
				);
				recommendations.forEach((day, index) => {
					const date = trip.days[index]?.date;
					if (date)
						Object.assign(
							trip,
							appendTripStops(
								trip,
								date,
								day.stops.map((place) => candidateToStop(place)),
							),
						);
				});
			}
			if (params.has("returnDraft")) {
				const rows = JSON.parse(
					sessionStorage.getItem("jgm-return-draft") ?? "[]",
				);
				if (!Array.isArray(rows) || !rows.every(isTripPlanItem))
					throw new Error("INVALID_TRIP");
				trip.candidates = rows.map((p) => ({
					...p,
					id: crypto.randomUUID(),
					completed: false,
					time: null,
					locked: false,
				}));
			}
			if (
				["baby", "parents", "friends", "other"].includes(
					params.get("companion") ?? "",
				)
			)
				trip.companionType = params.get("companion") as NonNullable<
					TripWorkspace["companionType"]
				>;
			if (params.has("packing")) {
				const selected = (params.get("packing") ?? "").split(",");
				for (const item of packingSuggestions(
					Number(params.get("packingMonth")) ||
						Number(trip.startDate.slice(5, 7)),
					trip.days.length,
					trip.companionType,
					trip.destination,
				).filter((p) => selected.includes(p.id)))
					trip.checklist.push({
						id: `packing-${item.id}`,
						title: item.title,
						group: "personal",
						done: false,
						officialUrl: null,
						dueOffsetDays: 3,
					});
			}
			if (pasteRows?.length) {
				Object.assign(trip, applyPastedTrip(trip, pasteRows));
				track("paste_import_used");
			}
			await saveTrip(trip);
			try { sessionStorage.removeItem(CREATE_DRAFT_KEY); } catch { /* 저장된 여행은 유지 */ }
			track("trip_created", {
				originAirport: trip.origin,
				destinationAirport: trip.destination,
			});
			window.location.assign(
				`/trips/${encodeURIComponent(trip.id)}${params.get("walk") ? "#itinerary" : ""}`,
			);
		} catch (reason) {
			const message = reason instanceof Error ? reason.message : "";
			setError(
				message.startsWith("INVALID_TRIP") ||
					message === "INVALID_EXCHANGE_RATE"
					? "여행 날짜·인원·예산을 확인해 주세요. 여행은 1일부터 31일까지 만들 수 있어요."
					: "이 기기에 여행을 저장하지 못했어요. 저장 공간과 사이트 데이터 허용을 확인한 뒤 다시 열어 주세요. 입력한 내용은 남겨 두었어요.",
			);
		} finally {
			submitting.current = false;
			setSaving(false);
		}
	};
	const companion = tripCompanions[destination as CompanionAirportCode];
	const durationPlan =
		cityGuides[destination].durations.find(
			(plan) => plan.days === requestedDays,
		) ?? cityGuides[destination].durations[2]!;
	const destinationChip = (code: AirportCode) => <label key={code}><input type="radio" name="destination" value={code} checked={destination === code} onChange={() => {
		const title = formRef.current?.elements.namedItem("title");
		if (title instanceof HTMLInputElement && title.value === `${editions[destination].city} 골목 여행`) title.value = `${editions[code].city} 골목 여행`;
		setDestination(code);
	}} /><span>{editions[code].city} · {code}</span></label>;
	const content = (
			<section className={`trip-create-shell${embedded ? " trip-create-embedded" : ""}`}>
				<div className="trip-create-copy paper-pattern">
					<a className="back-link" href="/trips">
						← 내 여행
					</a>
					<span className="eyebrow">{customerSectionLabels.trip.create}</span>
					<h1>
						검색한 여행을
						<br />내 것으로 이어 볼까요?
					</h1>
					<p>
						날짜와 공항만 정하면 {editions[destination].city} 추천 일정과 출발
						전 체크가 생겨요.
					</p>
					{getWalk(params.get("walk") ?? "") && (
						<p className="walk-carried-selection">
							고른 코스: {getWalk(params.get("walk") ?? "")?.title} · 여행
							첫날에 선택한 장소를 담아요.
						</p>
					)}
					<div className="destination-preview">
						<span className="iata">{destination}</span>
						<strong>
							{durationPlan.label} · {durationPlan.summary}
						</strong>
						<p>{durationPlan.rhythm}</p>
						<small>근교 범위 · {durationPlan.nearbyIncluded}</small>
					</div>
				</div>
				<section className="trip-create-form">
					<form ref={formRef} onSubmit={submit} onChange={(event) => saveDraft(event.currentTarget, event.target)}>
						<div className="form-intro">
							<span>먼저 3가지만 확인해요</span>
							<strong>어디서 떠나고, 어디로 가고, 언제 출발하는지</strong>
							<p>나머지는 추천값으로 먼저 만들고 언제든 바꿀 수 있어요.</p>
						</div>
						<fieldset className="airport-choice"><legend>출발공항</legend><div className="airport-chips">
							{origins.map((origin) => <label key={origin.iata}><input type="radio" name="origin" value={origin.iata} defaultChecked={origin.iata === initialOrigin} /><span>{origin.city} · {origin.iata}</span></label>)}
						</div></fieldset>
						<fieldset className="airport-choice"><legend>도착 도시</legend>
							<div className="airport-chips">{destinationCodes.filter((code, index) => index < 8 || code === initialFeaturedDestination).map(destinationChip)}</div>
							{destinationCodes.length > 8 && <details className="airport-more"><summary>다른 도시 더 보기</summary><div className="airport-chips">{destinationCodes.filter((code, index) => index >= 8 && code !== initialFeaturedDestination).map(destinationChip)}</div></details>}
						</fieldset>
						<label>
							가는 날
							<input
								name="startDate"
								type="date"
								value={startDate}
								onChange={(event) => {
									const next = event.target.value;
									setStartDate(next);
                                    if (pasteRows?.length) { setPasteRows(next && pasteText ? parsePastedTrip(pasteText, next).map(row => ({ ...row, localKey: crypto.randomUUID() })) : null); setError("날짜가 바뀌어 일정 미리보기를 다시 맞췄어요. 내용을 확인해 주세요."); }
									setEndDate(addCalendarDays(next, requestedDays - 1));
								}}
								required
							/>
							<small>
								돌아오는 날은 공항판에서 고른 {requestedDays}일 일정에 맞춰
								채워요.
							</small>
						</label>
                        <label>같이 가는 사람 <small>선택 · 내 이름부터 쉼표로 구분해요</small><input name="partyNames" maxLength={370} placeholder="민지, 준호, 유나" /></label>
						<details
							className="form-disclosure"
							onToggle={(event) => {
								if (event.currentTarget.open)
									track("trip_setup_open", { surface: "trip_create" });
							}}
						>
							<summary>기간·인원·예산을 바꾸고 싶어요</summary>
							<div className="form-disclosure-body">
								<label>
									여행 이름
									<input
										name="title"
										required
										maxLength={80}
										defaultValue={`${editions[destination].city} 골목 여행`}
									/>
								</label>
								<label>
									돌아오는 날
									<input
										name="endDate"
										type="date"
										value={endDate}
										onChange={(event) => setEndDate(event.target.value)}
										required
									/>
									<small>
										{requestedDays === 5
											? "공항판에서 고른 5일 기준, 더 늘려도 돼요."
											: `공항판에서 고른 ${requestedDays}일 기준으로 채웠어요.`}
									</small>
								</label>
								<div className="form-row">
									<label>
										함께 가는 사람
										<input
											name="partySize"
											type="number"
											min="1"
											max="12"
											defaultValue="1"
											required
										/>
									</label>
									<label>
										1인 하루 예산 · JPY
										<input
											name="dailyBudgetJpy"
											type="number"
											min="0"
											max="1000000"
											step="100"
											defaultValue="15000"
											required
										/>
									</label>
								</div>
								<label>
									내가 보는 환율 · 100엔당 원화{" "}
									<span className="optional">선택</span>
									<input
										name="krwPer100Jpy"
										type="number"
										min="100"
										max="10000"
										step="1"
										placeholder="예: 950"
									/>
									<small>
										실시간 환율이 아니며, 내가 적은 예산 환산에만 사용해요.
									</small>
								</label>
								<fieldset>
									<legend>
										이번 여행에서 더 보고 싶은 것{" "}
										<span className="optional">선택</span>
									</legend>
									<div className="choice-chips">
										{styleOptions.map((style) => (
											<label key={style.value}>
												<input
													type="checkbox"
													name="styles"
													value={style.value}
												/>
												<span>{style.label}</span>
											</label>
										))}
									</div>
								</fieldset>
							</div>
						</details>
						<details
							className="workspace-disclosure"
							open={params.has("paste") ? true : undefined}
						>
							<summary>붙여 넣어 만들기</summary>
							<p>
								1일차·Day 1·10/26 또는 시트의 날짜·시간·장소를 읽어요. 원문은
								저장하지 않아요.
							</p>
							<label>
								다른 앱의 일정 글
								<textarea
									maxLength={50000}
									rows={5}
									value={pasteText}
									onChange={(e) => {
										setPasteText(e.target.value);
										setPasteRows(null);
									}}
								/>
							</label>
							<button
								className="button secondary"
								type="button"
								onClick={() => {
									if (!startDate) {
										setError("가는 날을 먼저 골라 주세요.");
										return;
									}
									const rows = parsePastedTrip(pasteText, startDate);
									setPasteRows(
										rows.map((row) => ({
											...row,
											localKey: crypto.randomUUID(),
										})),
									);
									if (!rows.length)
										setError("읽을 장소가 없어요. 예: 1일차\n10:00 오호리공원");
								}}
							>
								붙인 일정 미리보기
							</button>
							{pasteRows?.map((row, i) => (
								<div className="form-row" key={row.localKey ?? row.title}>
									<label>
										장소
										<input
											maxLength={80}
											value={row.title}
											onChange={(e) =>
												setPasteRows(
													pasteRows.map((r, n) =>
														n === i ? { ...r, title: e.target.value } : r,
													),
												)
											}
										/>
									</label>
									<label>
										일차
										<input
											type="number"
											min={1}
											max={31}
											placeholder="날짜 없는 후보"
											value={row.day === null ? "" : row.day + 1}
											onChange={(e) =>
												setPasteRows(
													pasteRows.map((r, n) =>
														n === i
															? {
																	...r,
																	day: e.target.value
																		? Number(e.target.value) - 1
																		: null,
																}
															: r,
													),
												)
											}
										/>
									</label>
									<label>
										시각
										<input
											type="time"
											value={row.time ?? ""}
											onChange={(e) =>
												setPasteRows(
													pasteRows.map((r, n) =>
														n === i
															? { ...r, time: e.target.value || null }
															: r,
													),
												)
											}
										/>
									</label>
								</div>
							))}
						</details>
						{error && (
							<p className="inline-error" role="alert">
								{error}
							</p>
						)}
						<button className="button primary" type="submit" disabled={saving}>
							{saving ? "여행을 저장하는 중…" : "내 여행 열기"}
						</button>
						<p className="form-trust">
							입력한 여행 내용은 서버로 보내지 않고 이 기기에만 저장해요.
						</p>
					</form>
				</section>
			</section>
	);
	return embedded ? content : <Layout>{content}</Layout>;
}

export function TripDetailPage({
	id,
	publisherAd = disabledPublisherAdAdapter,
}: {
	id: string;
	publisherAd?: PublisherAdAdapter;
}) {
	const { state, persist, retry, saveError, canUndo, undoDeletion } =
		useTripWorkspace(id);
	const loadedTrip = state.status === "ready" ? state.trip : null;
	const [notice, setNotice] = useState("");
	const [activePanel, setActivePanel] = useState<TripPanel>(() =>
		panelFromHash(window.location.hash),
	);
	usePageMeta(
		loadedTrip ? `${loadedTrip.title} — 일본골목` : "내 여행 — 일본골목",
		"일정, 준비, 예산, 저장 목록과 현지 오늘 모드를 한곳에서 이어 가요.",
	);
	useEffect(() => {
		if (loadedTrip)
			track("trip_dashboard_open", {
				destinationAirport: loadedTrip.destination,
				tripStage: tripStage(loadedTrip, todayInSeoul()),
			});
	}, [loadedTrip?.id]);
	useEffect(() => {
		const syncPanel = () => setActivePanel(panelFromHash(window.location.hash));
		window.addEventListener("hashchange", syncPanel);
		return () => window.removeEventListener("hashchange", syncPanel);
	}, []);
	useEffect(() => {
		if (loadedTrip) track("trip_section_view", { surface: activePanel });
	}, [activePanel, loadedTrip?.id]);
	useEffect(() => {
		if (!loadedTrip || !window.location.hash || activePanel === "overview") return;
		const frame = requestAnimationFrame(() => {
			const target = document.getElementById(window.location.hash === "#calendar" ? "calendar" : activePanel);
			target?.scrollIntoView({ block: "start" });
			target?.focus({ preventScroll: true });
		});
		return () => cancelAnimationFrame(frame);
	}, [activePanel, loadedTrip?.id]);
	if (state.status === "loading") return <LoadingTrip />;
	if (state.status === "storage_error")
		return <TripStorageError retry={retry} />;
	if (state.status === "not_found") return <MissingTrip />;
	const trip = state.trip;
	const stage = tripStage(trip, todayInSeoul());
	const progress = tripProgress(trip);
	const heroSettlement = settleExpenses(trip);
	const heroParty = tripParty(trip);
	const heroName = (id: string) => heroParty.find((person) => person.id === id)?.nickname ?? "일행";
	const companion = tripCompanions[trip.destination as CompanionAirportCode];
	const edition = editions[trip.destination as AirportCode];
	const cityGuide = cityGuides[trip.destination as AirportCode];
	const durationPlan =
		cityGuide.durations.find(
			(plan) => plan.days === Math.min(5, trip.days.length),
		) ?? cityGuide.durations[4]!;
	const fareSummary = personalFareSummary(trip.fareNotes);
	const updateNotice = (message: string) => {
		setNotice(message);
		window.setTimeout(() => setNotice(""), 3000);
	};
	const openPanel = (panel: TripPanel, source = "nav") => {
		setActivePanel(panel);
		track("trip_section_open", { surface: source });
	};
	const addPlan = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const form = event.currentTarget;
		const data = new FormData(form);
		const date = String(data.get("date"));
		const item: TripPlanItem = {
			id: crypto.randomUUID(),
			type: String(data.get("type")) as TripPlanItem["type"],
			title: String(data.get("title")).trim().slice(0, 80),
			area: String(data.get("area")).trim().slice(0, 60),
			time: String(data.get("time")) || null,
			estimateJpy: Number(data.get("estimateJpy")) || null,
			mapQuery: String(data.get("mapQuery")).trim().slice(0, 100) || null,
			note: null,
			completed: false,
		};
		if (!item.title) return;
		if (
			!(await persist((draft) => {
				draft.days.find((day) => day.date === date)?.items.push(item);
			}))
		)
			return;
		form.reset();
		updateNotice("일정에 담았어요.");
		track("itinerary_item_added", {
			destinationAirport: trip.destination,
			itemType: item.type,
		});
	};
	const addChecklist = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const form = event.currentTarget;
		const title = String(new FormData(form).get("title")).trim().slice(0, 80);
		if (!title) return;
		if (
			!(await persist((draft) => {
				draft.checklist.push({
					id: crypto.randomUUID(),
					title,
					group: "personal",
					done: false,
					officialUrl: null,
				});
			}))
		)
			return;
		form.reset();
		updateNotice("준비 목록에 더했어요.");
	};
	const addExpense = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const form = event.currentTarget;
		const data = new FormData(form);
		const amountJpy = Number(data.get("amountJpy"));
		if (!Number.isInteger(amountJpy) || amountJpy <= 0) return;
		if (
			!(await persist((draft) => {
				draft.expenses.push({
					id: crypto.randomUUID(),
					title: String(data.get("title")).trim().slice(0, 80),
					category: String(
						data.get("category"),
					) as TripWorkspace["expenses"][number]["category"],
					amountJpy,
					occurredOn: String(data.get("occurredOn")),
				});
			}))
		)
			return;
		form.reset();
		updateNotice("지출을 기록했어요.");
		track("budget_expense_added", { destinationAirport: trip.destination });
	};
	const addFareNote = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const form = event.currentTarget;
		const data = new FormData(form);
		const amountKrw = Number(data.get("amountKrw"));
		if (!Number.isInteger(amountKrw) || amountKrw <= 0) {
			updateNotice("금액은 1원 이상 정수로 적어 주세요.");
			return;
		}
		if (
			!(await persist((draft) => {
				draft.fareNotes.push({
					id: crypto.randomUUID(),
					amountKrw,
					departureDate: String(data.get("departureDate")),
					observedOn: todayInSeoul(),
					seller: String(data.get("seller")).trim().slice(0, 60),
					baggage: String(data.get("baggage")).trim().slice(0, 60),
				});
			}))
		)
			return;
		form.reset();
		updateNotice("내 가격 메모에 남겼어요.");
		track("personal_fare_note_added", { destinationAirport: trip.destination });
	};
	const archive = async () => {
		if (
			!(await persist((draft) => {
				draft.stageOverride =
					draft.stageOverride === "archived" ? null : "archived";
			}))
		)
			return;
		updateNotice(
			trip.stageOverride === "archived"
				? "여행을 다시 꺼냈어요."
				: "다녀온 여행으로 보관했어요.",
		);
	};
	const todayReady = stage === "upcoming" || stage === "traveling";
	const nextAction =
		progress.plannedDays === 0
			? {
					href: "#itinerary",
					panel: "itinerary" as TripPanel,
					label: "추천 일정부터 담기",
					title: "먼저 여행의 뼈대를 만들어요.",
					description:
						"날짜별 추천을 한 번에 담은 뒤 필요 없는 곳만 빼면 가장 빨라요.",
				}
			: progress.checklistDone < progress.checklistTotal
				? {
						href: "#prepare",
						panel: "prepare" as TripPanel,
						label: "출발 준비 확인하기",
						title: "일정 다음은 빠뜨리기 쉬운 준비예요.",
						description: `기본 준비 ${progress.checklistTotal - progress.checklistDone}개가 아직 남아 있어요.`,
					}
				: todayReady
					? {
							href: `/trips/${encodeURIComponent(trip.id)}/today`,
							panel: null,
							label: "오늘 모드 열기",
							title: "이제 오늘 필요한 것만 크게 봐요.",
							description:
								"오늘 일정, 공항 첫 길, 지도와 긴급 경로를 짧게 모았어요.",
						}
					: trip.expenses.length === 0
						? {
								href: "#budget",
								panel: "budget" as TripPanel,
								label: "예산과 지출 보기",
								title: "계획 예산을 한 번 확인해요.",
								description:
									"출발 전에는 계획 기준을 보고, 여행 중에는 쓴 금액만 빠르게 남겨요.",
							}
						: {
								href: "#keep",
								panel: "keep" as TripPanel,
								label: "여행 보관하기",
								title: "다음 여행에서도 다시 쓸 수 있어요.",
								description:
									"백업을 받고 다녀온 여행으로 보관하면 일정 뼈대를 다시 꺼낼 수 있어요.",
							};
	const recommendationGrid = (
		<TripRecommendations trip={trip} persist={persist} />
	);
	return (
		<Layout>
			{canUndo && (
				<div role="status">
					지운 항목을 10초 동안 되살릴 수 있어요.{" "}
					<button type="button" onClick={() => void undoDeletion()}>
						되살리기
					</button>
				</div>
			)}
			<section className="trip-hero paper-pattern">
				<div>
					<a className="back-link" href="/trips">
						← 내 여행
					</a>
					<span className={`status-badge stage-${stage}`}>
						{stageCopy[stage]}
					</span>
					<h1>{trip.title}</h1>
					<p>
						{trip.origin}에서 {trip.destinationCity} ·{" "}
						{dateLabel(trip.startDate)}–{dateLabel(trip.endDate)} ·{" "}
						{trip.partySize}명
					</p>
					<JourneySummary summary={journeySummaryFromTrip(trip)} budgetHref="#budget" />
                    <TripEditor trip={trip} persist={persist} />
					<PartyEditor trip={trip} persist={persist} />
					<ShareSheet trip={trip} persist={persist} />
					<div className="trip-wide-itinerary" aria-label="날짜별 상세 일정">
						{trip.days.map((day, index) => <section key={day.date}>
							<h2>{index + 1}일차 · {dateLabel(day.date)}</h2>
							<ul>{day.items.filter((item) => !item.deferred).map((item) => <li key={item.id}>{item.time && <time>{item.time}</time>} {item.title}</li>)}</ul>
						</section>)}
					</div>
					<p className="trip-hero-guide">
						추천 일정 → 출발 준비 → 현지 오늘 · 지금 필요한 다음 단계만
						보여드려요.
					</p>
					<div className="button-row">
						<a
							className={
								trip.shareRevision ? "button primary" : "button secondary"
							}
							href={nextAction.href}
							onClick={() => {
								track("trip_next_action", { surface: "hero" });
								if (nextAction.panel) openPanel(nextAction.panel, "hero");
							}}
						>
							{nextAction.label}
						</a>
						<a
							className="button secondary"
							href={`/airports/${trip.destination.toLowerCase()}?from=${trip.origin}`}
						>
							{trip.destination} 공항판
						</a>
						{todayReady && nextAction.panel && (
							<a
								className="button text"
								href={`/trips/${encodeURIComponent(trip.id)}/today`}
							>
								오늘 모드
							</a>
						)}
					</div>
				</div>
				<aside className="trip-overview">
					<span className="eyebrow">{customerSectionLabels.trip.overview}</span>
					<dl>
						<div>
							<dt>준비</dt>
							<dd>
								{progress.checklistDone}/{progress.checklistTotal}
							</dd>
						</div>
						<div>
							<dt>일정</dt>
							<dd>
								{progress.plannedDays}/{progress.totalDays}일
							</dd>
						</div>
						<div>
							<dt>지출</dt>
							<dd>{money(progress.spentJpy, "JPY")}</dd>
						</div>
					</dl>
					<h2>출발 전 준비</h2>
					<ul>{trip.checklist.slice(0, 5).map((item) => <li key={item.id}>
						<label><input type="checkbox" checked={item.done} onChange={(event) => {
							const checked = event.currentTarget.checked;
							void persist((draft) => { const entry = draft.checklist.find((candidate) => candidate.id === item.id); if (entry) entry.done = checked; });
						}} /> {item.title}</label>{item.assignee && <span> · {heroName(item.assignee)}</span>}
					</li>)}</ul>
					<a href="#prepare" onClick={() => openPanel("prepare", "hero")}>준비 항목 보기 →</a>
					<h2>돈 · 누가 누구에게</h2>
					{heroSettlement.rows.length ? heroSettlement.rows.map((row) => <p key={`${row.from}:${row.to}`}>{heroName(row.from)} → {heroName(row.to)} · ¥{row.amountJpy.toLocaleString()}</p>) : <p>지출을 남기면 일행 정산을 볼 수 있어요.</p>}
					{heroSettlement.excluded > 0 && <p>환율 없는 원화 지출은 제외됐어요.</p>}
					<a href="#budget" onClick={() => openPanel("budget", "hero")}>정산과 근거 보기 →</a>
					<p>{companion.paceNote}</p>
				</aside>
			</section>
			<nav className="trip-section-nav" aria-label="여행 작업">
				<a
					href="#overview"
					aria-current={activePanel === "overview" ? "page" : undefined}
					onClick={() => openPanel("overview")}
				>
					한눈에
				</a>
				{tripPanels.slice(1).map((panel) => (
					<a
						href={`#${panel.id}`}
						aria-current={activePanel === panel.id ? "page" : undefined}
						onClick={() => openPanel(panel.id)}
						key={panel.id}
					>
						{panel.label}
					</a>
				))}
				<a href={`/trips/${encodeURIComponent(trip.id)}/film`}>골목 필름</a>
			</nav>
			<LocalSaveStatus updatedAt={trip.updatedAt} error={saveError} />
			{notice && (
				<div className="toast" role="status">
					{notice}
				</div>
			)}
			<section
				className="section trip-dashboard-panel"
				id="overview"
				hidden={activePanel !== "overview"}
				aria-labelledby="trip-overview-title"
			>
				<details className="workspace-disclosure trip-overview-guide">
					<summary>여행 단계와 다음 행동 보기</summary>
				<div className="workspace-intro">
					<div>
						<span className="eyebrow">이 여행은 이렇게 준비해요</span>
						<h2 id="trip-overview-title">
							일정부터 현지 오늘까지,
							<br />
							지금 필요한 것만 열어요.
						</h2>
						<p>
							모든 칸을 채울 필요는 없어요. 일본골목은 다음 할 일을 하나씩 보여
							주고, 입력한 내용은 이 기기에만 보관해요.
						</p>
					</div>
					<aside className="next-action-card">
						<span>지금 할 일</span>
						<h3>{nextAction.title}</h3>
						<p>{nextAction.description}</p>
						<a
							className="button primary"
							href={nextAction.href}
							onClick={() => {
								track("trip_next_action", { surface: "overview" });
								if (nextAction.panel) openPanel(nextAction.panel, "overview");
							}}
						>
							{nextAction.label}
						</a>
					</aside>
				</div>
				<ol className="trip-step-grid">
					<li
						className={progress.plannedDays > 0 ? "is-complete" : "is-current"}
					>
						<span>1</span>
						<div>
							<strong>추천으로 일정 시작</strong>
							<p>하루씩 직접 쓰기 전에 도시별 추천을 초안으로 담아요.</p>
							<a
								href="#itinerary"
								onClick={() => openPanel("itinerary", "step")}
							>
								{progress.plannedDays > 0
									? `일정 ${progress.plannedDays}/${progress.totalDays}일 보기`
									: "추천 일정 담기"}
							</a>
						</div>
					</li>
					<li
						className={
							progress.checklistDone === progress.checklistTotal
								? "is-complete"
								: progress.plannedDays > 0
									? "is-current"
									: ""
						}
					>
						<span>2</span>
						<div>
							<strong>출발 준비 확인</strong>
							<p>입국·보험·결제·통신처럼 빠뜨리기 쉬운 것만 확인해요.</p>
							<a href="#prepare" onClick={() => openPanel("prepare", "step")}>
								준비 {progress.checklistDone}/{progress.checklistTotal} 보기
							</a>
						</div>
					</li>
					<li className={trip.expenses.length > 0 ? "is-complete" : ""}>
						<span>3</span>
						<div>
							<strong>예산은 필요할 때 기록</strong>
							<p>계획 예산을 보고, 여행 중 쓴 금액만 간단히 남겨요.</p>
							<a href="#budget" onClick={() => openPanel("budget", "step")}>
								{trip.expenses.length
									? `지출 ${trip.expenses.length}건 보기`
									: "예산 확인하기"}
							</a>
						</div>
					</li>
					<li className={todayReady ? "is-current" : ""}>
						<span>4</span>
						<div>
							<strong>출발하면 오늘 모드</strong>
							<p>오늘 일정과 첫 길, 지도, 긴급 경로만 크게 열어요.</p>
							{todayReady ? (
								<a href={`/trips/${encodeURIComponent(trip.id)}/today`}>
									오늘 모드 열기
								</a>
							) : (
								<small>출발이 가까워지면 여기서 안내해요.</small>
							)}
						</div>
					</li>
				</ol>
				</details>
				<InstallPrompt />
				<aside className="local-data-note">
					<strong>보내기를 누른 내용만 링크에 담겨요.</strong>
					<p>
						이 여행은 이 기기에 저장돼요. 공유할 때는 내용을 먼저 확인해 주세요.
						받은 링크는 여행을 만든 앱 안에서 붙여 넣어 열 수 있어요.
					</p>
					<a href="#keep" onClick={() => openPanel("keep", "overview")}>
						백업과 공유 방법 보기 →
					</a>
				</aside>
			</section>
			<section
				className="section trip-workspace trip-panel"
				id="itinerary"
				hidden={activePanel !== "itinerary"}
			>
				<div className="section-heading">
					<div>
						<span className="eyebrow">
							{customerSectionLabels.trip.itinerary}
						</span>
						<h2>날짜마다 무리 없이 담아요.</h2>
					</div>
					<p>
						장소의 실시간 영업 여부는 지도와 공식 페이지에서 마지막으로
						확인해요.
					</p>
				</div>
				<div className="trip-duration-note">
					<span className="eyebrow">{durationPlan.label}</span>
					<strong>{durationPlan.summary}</strong>
					<p>
						{durationPlan.rhythm} · 근교 범위: {durationPlan.nearbyIncluded}
					</p>
				</div>
				{progress.itineraryTotal === 0 && recommendationGrid}
				<StayEditor trip={trip} persist={persist} />
				<TripContextEditor trip={trip} persist={persist} />
				<TripCandidates trip={trip} persist={persist} />
				<SavedWalkSuggestions trip={trip} persist={persist} />
				<div className="day-stack">
					{trip.days.map((day, dayIndex) => (
						<article className="day-card" key={day.date}>
							<header>
								<div>
									<span>{tripDayLabel(dayIndex + 1)}</span>
									<h3>{dateLabel(day.date)}</h3>
								</div>
							</header>
							<DayMap day={day} />
							<DaySwap trip={trip} date={day.date} persist={persist} />
							<DayPlan trip={trip} day={day} persist={persist} />
						</article>
					))}
				</div>
				{progress.itineraryTotal > 0 && (
					<details className="workspace-disclosure">
						<summary>지역 추천 일정 다시 보기</summary>
						{recommendationGrid}
					</details>
				)}
				<details
					className="workspace-disclosure"
					onToggle={(event) => {
						if (event.currentTarget.open)
							track("itinerary_manual_open", { surface: "itinerary" });
					}}
				>
					<summary>직접 일정 하나 추가하기</summary>
					<div className="editor-panel">
						<p className="editor-help">
							이름만 필수예요. 시간·동네·비용·지도 검색어는 알고 있을 때만
							적으세요.
						</p>
						<form className="compact-form" onSubmit={addPlan}>
							<div className="form-row">
								<label>
									날짜
									<select name="date">
										{trip.days.map((day) => (
											<option key={day.date}>{day.date}</option>
										))}
									</select>
								</label>
								<label>
									종류
									<select name="type">
										<option value="spot">가볼 곳</option>
										<option value="food">먹을 것</option>
										<option value="transport">이동</option>
										<option value="memo">메모</option>
									</select>
								</label>
								<label>
									시간 <span className="optional">선택</span>
									<input name="time" type="time" />
								</label>
							</div>
							<label>
								이름
								<input
									name="title"
									required
									maxLength={80}
									placeholder="예: 오호리공원 산책"
								/>
							</label>
							<details className="nested-disclosure">
								<summary>동네·비용·지도 검색어도 적기</summary>
								<div className="form-row">
									<label>
										동네
										<input
											name="area"
											maxLength={60}
											placeholder={trip.destinationCity}
										/>
									</label>
									<label>
										예상 비용 · JPY
										<input
											name="estimateJpy"
											type="number"
											min="0"
											step="100"
										/>
									</label>
									<label>
										지도 검색어
										<input
											name="mapQuery"
											maxLength={100}
											placeholder="장소명 또는 주소"
										/>
									</label>
								</div>
							</details>
							<button className="button secondary" type="submit">
								일정에 담기
							</button>
						</form>
					</div>
				</details>
			</section>
			<section
				className="section prepare-section trip-panel"
				id="prepare"
				hidden={activePanel !== "prepare"}
			>
				<div className="section-heading">
					<div>
						<span className="eyebrow">
							{customerSectionLabels.trip.prepare}
						</span>
						<h2>출발 전에 필요한 것만 확인해요.</h2>
					</div>
					<p>
						여권 번호나 예약번호는 적지 마세요. 공식 서비스는 새 창에서 직접
						입력해요.
					</p>
				</div>
				<PreparationTools trip={trip} persist={persist} />
				<div className="prepare-layout">
					<div className="checklist">
						{trip.checklist.map((item) => (
							<article className={item.done ? "is-done" : ""} key={item.id}>
								<button
									className="check-button"
									type="button"
									aria-label={`${item.title} ${item.done ? "미완료로" : "완료로"}`}
									aria-pressed={item.done}
									onClick={() => {
										persist((draft) => {
											const target = draft.checklist.find(
												(entry) => entry.id === item.id,
											);
											if (target) target.done = !target.done;
										});
										track("checklist_toggled", {
											destinationAirport: trip.destination,
										});
									}}
								>
									{item.done ? "✓" : ""}
								</button>
								<div>
									<strong>{item.title}</strong>
									{item.officialUrl && (
										<ExternalLink href={item.officialUrl} surface="prepare">
											공식 안내
										</ExternalLink>
									)}
								</div>
								{trip.partySize > 1 && (
									<label>
										담당
										<select
											value={item.assignee ?? ""}
											onChange={(e) =>
												void persist((d) => {
													const x = d.checklist.find((c) => c.id === item.id);
													if (x) x.assignee = e.target.value || undefined;
												})
											}
										>
											<option value="">미정</option>
											{(
												trip.party ??
												Array.from({ length: trip.partySize }, (_, i) => ({
													id: i === 0 ? "me" : `party-${i}`,
													nickname: i === 0 ? "나" : `일행${i}`,
												}))
											).map((p) => (
												<option value={p.id} key={p.id}>
													{p.nickname}
												</option>
											))}
										</select>
									</label>
								)}
								{item.group === "personal" && (
									<button
										className="text-button danger"
										type="button"
										onClick={() =>
											persist((draft) => {
												draft.checklist = draft.checklist.filter(
													(entry) => entry.id !== item.id,
												);
											})
										}
									>
										삭제
									</button>
								)}
							</article>
						))}
						<form className="inline-form" onSubmit={addChecklist}>
							<label className="sr-only" htmlFor="custom-check">
								준비 항목
							</label>
							<input
								id="custom-check"
								name="title"
								maxLength={80}
								placeholder="내 준비 항목"
								required
							/>
							<button className="button secondary" type="submit">
								더하기
							</button>
						</form>
					</div>
					<aside className="official-tools">
						<span className="eyebrow">
							{customerSectionLabels.trip.officialTools}
						</span>
						<h3>공식 경로를 한곳에</h3>
						{officialTravelTools.slice(0, 3).map((tool) => (
							<ExternalLink href={tool.url} key={tool.id} surface="prepare">
								<strong>{tool.title}</strong>
								<span>{tool.description}</span>
							</ExternalLink>
						))}
						<SourceStamp
							source="공식 안전·입국 경로"
							checkedAt="2026-08-10T00:00:00+09:00"
						/>
					</aside>
				</div>
			</section>
			<section
				className="section trip-panel"
				id="budget"
				tabIndex={-1}
				hidden={activePanel !== "budget"}
			>
				<MoneyPanel trip={trip} persist={persist} />
			</section>
			<section
				className="section fare-note-section trip-panel"
				id="fare-notes"
				hidden={activePanel !== "budget"}
			>
				<div className="section-heading">
					<div>
						<span className="eyebrow">
							{customerSectionLabels.trip.fareNotes}
						</span>
						<h2>
							항공권을 비교 중일 때만
							<br />본 가격을 메모해요.
						</h2>
					</div>
					<p>
						자동 가격 비교가 아니라 판매처·수하물 조건이 다른 개인 메모예요.
					</p>
				</div>
				<div className="fare-note-explainer">
					<strong>꼭 써야 하나요?</strong>
					<p>
						아니요. 항공권을 이미 샀거나 따로 비교하지 않는다면 건너뛰세요. 적은
						값은 시장 최저가로 판단하지 않아요.
					</p>
				</div>
				<div className="fare-note-layout">
					<details
						className="workspace-disclosure fare-disclosure"
						open={fareSummary.state === "empty" ? true : undefined}
					>
						<summary>본 가격 하나 메모하기</summary>
						<form
							className="editor-panel compact-form"
							noValidate
							onSubmit={addFareNote}
						>
							<label>
								왕복·편도 표시 가격 · KRW
								<input
									name="amountKrw"
									type="number"
									min="1"
									inputMode="numeric"
									step="1"
									required
								/>
							</label>
							<div className="form-row">
								<label>
									출발일
									<input
										name="departureDate"
										type="date"
										defaultValue={trip.startDate}
										required
									/>
								</label>
								<label>
									본 곳
									<input
										name="seller"
										maxLength={60}
										placeholder="항공사·판매처"
									/>
								</label>
							</div>
							<label>
								수하물 조건
								<input
									name="baggage"
									maxLength={60}
									placeholder="예: 위탁 15kg 포함"
								/>
							</label>
							<button className="button secondary" type="submit">
								내 가격 메모 남기기
							</button>
						</form>
					</details>
					<aside className="personal-fare-summary">
						<span className="status-badge pending">
							개인 메모 · 시장 통계 아님
						</span>
						{fareSummary.state === "empty" ? (
							<>
								<h3>적어 둔 가격이 없어요.</h3>
								<p>비교 중이라면 조건과 함께 남겨 보세요.</p>
							</>
						) : (
							<>
								<h3>{fareSummary.n}번 적어 본 범위</h3>
								<dl>
									<div>
										<dt>낮게 본 값</dt>
										<dd>{money(fareSummary.minKrw!, "KRW")}</dd>
									</div>
									<div>
										<dt>가운데 값</dt>
										<dd>{money(fareSummary.medianKrw!, "KRW")}</dd>
									</div>
									<div>
										<dt>높게 본 값</dt>
										<dd>{money(fareSummary.maxKrw!, "KRW")}</dd>
									</div>
								</dl>
								<p>
									표본이 적고 조건이 달라 ‘저렴한 달·요일’ 결론에는 쓰지 않아요.
								</p>
							</>
						)}
					</aside>
				</div>
				<div className="fare-note-list">
					{trip.fareNotes.map((note) => (
						<article key={note.id}>
							<span>
								{note.observedOn}에 봄 · 출발 {note.departureDate}
							</span>
							<strong>{money(note.amountKrw, "KRW")}</strong>
							<p>
								{note.seller || "판매처 미기록"} ·{" "}
								{note.baggage || "수하물 미확인"}
							</p>
							<button
								className="text-button danger"
								type="button"
								onClick={() =>
									persist((draft) => {
										draft.fareNotes = draft.fareNotes.filter(
											(item) => item.id !== note.id,
										);
									})
								}
							>
								삭제
							</button>
						</article>
					))}
				</div>
			</section>
			<section
				className="section keep-section trip-panel"
				id="keep"
				hidden={activePanel !== "keep"}
			>
				<SummaryCard trip={trip} />
				<div>
					<span className="eyebrow">{customerSectionLabels.trip.keep}</span>
					<h2>돌아온 뒤에도 다음 여행의 시작으로 남겨요.</h2>
					<p>
						골목 필름은 일정의 날짜·동네·가게와 내가 고른 사진만 이 기기에서
						이어요. 파일로 보낼 날짜·금액·사진·메모는 아래에서 직접 고르고 먼저
						확인할 수 있어요.
					</p>
				</div>
				<div className="keep-actions">
					<a
						className="button primary"
						href={`/trips/${encodeURIComponent(trip.id)}/film`}
					>
						골목 필름 만들기
					</a>
					<button className="button text" type="button" onClick={archive}>
						{trip.stageOverride === "archived"
							? "보관에서 꺼내기"
							: "다녀온 여행으로 보관"}
					</button>
					<button
						className="text-button danger"
						type="button"
						onClick={async () => {
							if (
								!window.confirm(
									"이 여행을 이 기기에서 지울까요? 먼저 여행 백업 파일을 받을 수 있어요.",
								)
							)
								return;
							await trashTrip(trip);
							window.location.assign("/trips");
						}}
					>
						여행 삭제
					</button>
				</div>
			</section>
			<section className="section" hidden={activePanel !== "keep"}>
				<TripExportPanel trip={trip} />
				<TripClonePanel trip={trip} />
			</section>
			<StandardPublisherAdSlot
				archived={trip.stageOverride === "archived"}
				adapter={publisherAd}
			/>
		</Layout>
	);
}

export function TripTodayPage({ id }: { id: string }) {
	const { state, persist, retry, saveError, canUndo, undoDeletion } =
		useTripWorkspace(id);
	const [selectedDate, setSelectedDate] = useState("");
	const loadedTrip = state.status === "ready" ? state.trip : null;
	usePageMeta(
		loadedTrip
			? `${loadedTrip.title} 오늘 — 일본골목`
			: "여행 오늘 모드 — 일본골목",
		"오늘 일정, 공항 첫 길, 지도, 일본어 표현과 긴급 경로를 짧게 열어요.",
	);
	useEffect(() => {
		if (loadedTrip)
			track("today_mode_open", {
				destinationAirport: loadedTrip.destination,
				tripStage: tripStage(loadedTrip, todayInSeoul()),
			});
	}, [loadedTrip?.id]);
	if (state.status === "loading") return <LoadingTrip />;
	if (state.status === "storage_error")
		return <TripStorageError retry={retry} />;
	if (state.status === "not_found") return <MissingTrip />;
	const trip = state.trip;
	const today = todayInSeoul();
	const activeDate =
		today < trip.startDate
			? trip.startDate
			: today > trip.endDate
				? trip.endDate
				: today;
	const day =
		trip.days.find((item) => item.date === (selectedDate || activeDate)) ??
		trip.days[0]!;
	const edition = editions[trip.destination as AirportCode];
	const firstBranch = edition.branches[0]!;
	const currentWeatherUrl = `https://www.jma.go.jp/bosai/forecast/#lang=en`;
	return (
		<Layout>
			<section className="today-hero">
				<a className="back-link" href={`/trips/${encodeURIComponent(trip.id)}`}>
					← 여행 전체
				</a>
				<span className="eyebrow">{customerSectionLabels.trip.today}</span>
				<h1>{trip.destinationCity}의 오늘</h1>
				<div className="today-date-picker">
					<label htmlFor="today-date">볼 날짜</label>
					<select
						id="today-date"
						value={day.date}
						onChange={(event) => setSelectedDate(event.target.value)}
					>
						{trip.days.map((entry, index) => (
							<option key={entry.date} value={entry.date}>
								{index + 1}일차 · {dateLabel(entry.date)}
							</option>
						))}
					</select>
				</div>
				<p>
					{day.date === today
						? "오늘 필요한 것만 크게 모았어요."
						: day.date > today
							? "선택한 여행 날짜를 미리 보고 있어요."
							: "선택한 날짜의 기록을 다시 보고 있어요."}
				</p>
				<div className="offline-badge">
					<span /> 일정과 메모는 이 기기에서 열려요 · 실시간 교통·지도는 연결이
					필요해요
				</div>
			</section>
			<LocalSaveStatus updatedAt={trip.updatedAt} error={saveError} />
			<section className="today-grid section">
				<article className="today-main">
					<DayMap day={day} />
					<DaySwap trip={trip} date={day.date} persist={persist} />
					<DayPlan trip={trip} day={day} persist={persist} today />
					{day.items.length === 0 && (
						<a
							className="button secondary"
							href={`/trips/${trip.id}#itinerary`}
						>
							일정 담으러 가기
						</a>
					)}
					<a className="button text" href={`/trips/${trip.id}/film`}>
						우연히 만난 장면 남기기 →
					</a>
				</article>
				<aside className="arrival-today">
					<details>
						<summary>공항에서 첫 이동 다시 보기</summary>
						<span className="iata">{trip.destination}</span>
						<h2>공항에서 {firstBranch.destination}</h2>
						<strong>
							{firstBranch.mode} · {firstBranch.minutes}
						</strong>
						<p>{firstBranch.boarding}</p>
						<p className="fallback">
							<strong>놓쳤다면</strong> {firstBranch.fallback}
						</p>
						<ExternalLink
							className="button secondary"
							href={edition.accessSource}
							surface="today_arrival"
						>
							현재 공식 시간표
						</ExternalLink>
					</details>
				</aside>
			</section>
			<TodayTools trip={trip} persist={persist} />
			<InstallPrompt />
			<ReturnTime trip={trip} persist={persist} />
			<section className="section">
				<MoneyPanel trip={trip} persist={persist} quick date={day.date} />
			</section>
			<section className="section emergency-section">
				<div className="emergency-call">
					<span className="eyebrow">
						{customerSectionLabels.trip.emergency}
					</span>
					<h2>급할 때는 검색보다 번호부터.</h2>
					<div>
						<ExternalLink
							href="tel:110"
							surface="emergency_phone"
							ariaLabel="경찰 긴급전화 110"
						>
							<span>경찰</span>
							<strong>110</strong>
						</ExternalLink>
						<ExternalLink
							href="tel:119"
							surface="emergency_phone"
							ariaLabel="구급·화재 긴급전화 119"
						>
							<span>구급·화재</span>
							<strong>119</strong>
						</ExternalLink>
					</div>
					<p>
						전화가 어렵거나 여행자 지원이 필요하면 JNTO 24시간 Visitor Hotline{" "}
						<ExternalLink href="tel:+815038162787" surface="emergency_phone">
							050-3816-2787
						</ExternalLink>
						을 이용해요.
					</p>
					<ExternalLink
						href="https://www.japan.travel/en/plan/emergencies/"
						surface="emergency"
					>
						JNTO 공식 안전 안내
					</ExternalLink>
				</div>
				<div className="phrase-board">
					<span className="eyebrow">{customerSectionLabels.trip.phrases}</span>
					<h2>보여 주고 말해요.</h2>
					{essentialPhrases.map((phrase) => (
						<details key={phrase.ja}>
							<summary>{phrase.ko}</summary>
							<strong lang="ja">{phrase.ja}</strong>
							<p>{phrase.reading}</p>
						</details>
					))}
				</div>
			</section>
			<section className="section official-today">
				<div className="section-heading">
					<div>
						<span className="eyebrow">
							{customerSectionLabels.trip.liveCheck}
						</span>
						<h2>연결될 때 공식 현재 상태를 확인해요.</h2>
					</div>
					<p>일본골목은 실시간 재난·교통을 추정하지 않아요.</p>
				</div>
				<div className="official-tool-grid">
					<ExternalLink href={currentWeatherUrl} surface="today_live">
						<strong>기상·지진·태풍</strong>
						<span>일본 기상청</span>
					</ExternalLink>
					{officialTravelTools
						.filter((tool) =>
							["navitime", "jnto-safety", "maps-offline"].includes(tool.id),
						)
						.map((tool) => (
							<ExternalLink href={tool.url} key={tool.id} surface="today_live">
								<strong>{tool.title}</strong>
								<span>{tool.description}</span>
							</ExternalLink>
						))}
				</div>
				<SourceStamp
					source="공식 안전·교통 연결"
					checkedAt="2026-08-10T00:00:00+09:00"
				/>
			</section>
		</Layout>
	);
}
